import React, { useMemo } from "react";
import parse from "html-react-parser";
import { FaChevronLeft, FaChevronRight, FaInbox } from "react-icons/fa";
import { useMediaQuery } from "react-responsive";
import {
  AvatarFallback,
  BalanceBadge,
  CountryText,
  DateText,
  EmailText,
  EmptyState,
  FooterInfo,
  MobileCard,
  MobileCardBadgeRow,
  MobileCardDate,
  MobileCardEmail,
  MobileCardField,
  MobileCardFooter,
  MobileCardGrid,
  MobileCardInfo,
  MobileCardName,
  MobileCardTop,
  MobileFieldLabel,
  MobileFieldValue,
  MobileList,
  PageBtn,
  PageDots,
  PaginationGroup,
  RowIndex,
  StatCell,
  StatusBadge,
  StyledTable,
  TBody,
  TD,
  TestTitleWrap,
  TH,
  THead,
  TableCard,
  TableFooter,
  TR,
  UserAvatar,
  UserNameCell,
  UserNameText,
} from "./UserTableStyles";

const isMobileQuery = { query: "(max-width: 768px)" };

const initials = (name = "") =>
  name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const Avatar = ({ src, name }) =>
  src ? (
    <UserAvatar>
      <img src={src} alt={name} />
    </UserAvatar>
  ) : (
    <AvatarFallback>{initials(name)}</AvatarFallback>
  );

// Build visible page numbers: always show first, last, current ±1, with dots
const buildPages = (current, total) => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set(
    [1, total, current, current - 1, current + 1].filter(
      (p) => p >= 1 && p <= total
    )
  );
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  let prev = 0;
  for (const p of sorted) {
    if (p - prev > 1) result.push("…");
    result.push(p);
    prev = p;
  }
  return result;
};

export const Users = ({
  data,
  meta,
  currentPage,
  onGotoNextPage,
  onGotoPrevPage,
  onGotoPage,
  isBusiness,
  embedded = false,
}) => {
  const isMobile = useMediaQuery(isMobileQuery);
  const totalPages =
    meta?.totalPages ||
    Math.ceil((meta?.totalItems || 0) / (meta?.pageSize || 10));
  const totalItems = meta?.totalItems || 0;
  const pageSize = meta?.pageSize || 10;
  const from = totalItems ? (currentPage - 1) * pageSize + 1 : 0;
  const to = Math.min(currentPage * pageSize, totalItems);

  const pages = useMemo(
    () => buildPages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  if (!data?.length) {
    return (
      <TableCard $embedded={embedded}>
        <EmptyState>
          <FaInbox size={36} color="#e2e8f0" />
          <span>No users found</span>
        </EmptyState>
      </TableCard>
    );
  }

  // ── Mobile card list ────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <>
        <TableCard $embedded={embedded}>
          <MobileList>
            {data.map((u, idx) => (
              <MobileCard key={u.id || u.DT_RowId || idx} $idx={idx}>
                <MobileCardTop>
                  <Avatar src={u.img} name={u.name} />
                  <MobileCardInfo>
                    <MobileCardName>{u.name}</MobileCardName>
                    <MobileCardEmail>{u.email}</MobileCardEmail>
                  </MobileCardInfo>
                  <StatusBadge $status={u.status}>{u.status}</StatusBadge>
                </MobileCardTop>

                <MobileCardBadgeRow>
                  <BalanceBadge $val={u.balance}>{u.balance} Units</BalanceBadge>
                  {u.country && <CountryText>🌍 {u.country}</CountryText>}
                </MobileCardBadgeRow>

                <MobileCardGrid>
                  <MobileCardField $full>
                    <MobileFieldLabel>School</MobileFieldLabel>
                    <MobileFieldValue>{u.schoolName || "—"}</MobileFieldValue>
                  </MobileCardField>
                  <MobileCardField>
                    <MobileFieldLabel>Attempts</MobileFieldLabel>
                    <MobileFieldValue>{u.attempts ?? "—"}</MobileFieldValue>
                  </MobileCardField>
                  <MobileCardField>
                    <MobileFieldLabel>Tests Done</MobileFieldLabel>
                    <MobileFieldValue>{u.test_count ?? "—"}</MobileFieldValue>
                  </MobileCardField>
                </MobileCardGrid>

                <MobileCardFooter>
                  <MobileCardDate>Joined {u.createdAt}</MobileCardDate>
                  <RowIndex>#{u.DT_RowId ?? idx + 1}</RowIndex>
                </MobileCardFooter>
              </MobileCard>
            ))}
          </MobileList>

          <TableFooter $embedded={embedded}>
            <FooterInfo>
              {from}–{to} of {totalItems} users
            </FooterInfo>
            <PaginationGroup>
              <PageBtn type="button" onClick={onGotoPrevPage} disabled={currentPage <= 1}>
                <FaChevronLeft size={11} />
              </PageBtn>
              <PageBtn type="button" onClick={onGotoNextPage} disabled={currentPage >= totalPages}>
                <FaChevronRight size={11} />
              </PageBtn>
            </PaginationGroup>
          </TableFooter>
        </TableCard>
      </>
    );
  }

  // ── Desktop table ───────────────────────────────────────────────────────────
  return (
    <TableCard $embedded={embedded}>
      <div style={{ overflowX: "auto" }}>
        <StyledTable>
          <THead>
            <tr>
              <TH>#</TH>
              <TH>User</TH>
              <TH>Email</TH>
              <TH>Status</TH>
              <TH>Balance</TH>
              <TH>School</TH>
              <TH style={{ textAlign: "center" }}>Attempts</TH>
              <TH style={{ textAlign: "center" }}>Tests Done</TH>
              <TH>Country</TH>
              <TH>Test Title</TH>
              <TH>Signed Up</TH>
            </tr>
          </THead>
          <TBody>
            {data.map((u, idx) => (
              <TR key={u.id || u.DT_RowId || idx} $idx={idx}>
                <TD>
                  <RowIndex>{u.DT_RowId ?? idx + 1}</RowIndex>
                </TD>

                <TD>
                  <UserNameCell>
                    <Avatar src={u.img} name={u.name} />
                    <UserNameText>{u.name}</UserNameText>
                  </UserNameCell>
                </TD>

                <TD>
                  <EmailText>{u.email}</EmailText>
                </TD>

                <TD>
                  <StatusBadge $status={u.status}>{u.status}</StatusBadge>
                </TD>

                <TD>
                  <BalanceBadge $val={u.balance}>{u.balance}</BalanceBadge>
                </TD>

                <TD style={{ maxWidth: 150 }}>
                  {u.schoolName || (
                    <span style={{ color: "#cbd5e1" }}>—</span>
                  )}
                </TD>

                <TD style={{ textAlign: "center" }}>
                  <StatCell>
                    <span>{u.attempts ?? 0}</span>
                    <span>Tries</span>
                  </StatCell>
                </TD>

                <TD style={{ textAlign: "center" }}>
                  <StatCell>
                    <span>{u.test_count ?? 0}</span>
                    <span>Done</span>
                  </StatCell>
                </TD>

                <TD>
                  <CountryText>
                    {u.country || <span style={{ color: "#cbd5e1" }}>—</span>}
                  </CountryText>
                </TD>

                <TD>
                  {u.test_title ? (
                    <TestTitleWrap>
                      {parse(u.test_title.main || "")}
                      {u.test_title.more && (
                        <span
                          style={{
                            color: "#2f67d8",
                            fontSize: 11,
                            fontFamily: "rebondG-Semi-Bold",
                          }}
                        >
                          {" "}
                          …more
                        </span>
                      )}
                    </TestTitleWrap>
                  ) : (
                    <span style={{ color: "#cbd5e1" }}>—</span>
                  )}
                </TD>

                <TD>
                  <DateText>{u.createdAt}</DateText>
                </TD>
              </TR>
            ))}
          </TBody>
        </StyledTable>
      </div>

      <TableFooter $embedded={embedded}>
        <FooterInfo>
          Showing <strong>{from}</strong>–<strong>{to}</strong> of{" "}
          <strong>{totalItems}</strong> users
        </FooterInfo>

        <PaginationGroup>
          <PageBtn type="button" onClick={onGotoPrevPage} disabled={currentPage <= 1}>
            <FaChevronLeft size={11} />
          </PageBtn>

          {pages.map((p, i) =>
            p === "…" ? (
              <PageDots key={`dots-${i}`}>…</PageDots>
            ) : (
              <PageBtn
                key={p}
                type="button"
                $active={p === currentPage}
                onClick={() => {
                  if (onGotoPage) onGotoPage(p);
                  else if (p > currentPage) onGotoNextPage();
                  else if (p < currentPage) onGotoPrevPage();
                }}
              >
                {p}
              </PageBtn>
            )
          )}

          <PageBtn type="button" onClick={onGotoNextPage} disabled={currentPage >= totalPages}>
            <FaChevronRight size={11} />
          </PageBtn>
        </PaginationGroup>
      </TableFooter>
    </TableCard>
  );
};
